<template>
  <nav
    aria-label="Search Results Pagination"
    class="mt-8"
  >
    <AisPagination
      :padding="2"
      class="flex items-center justify-between pt-5 text-sm border-t border-gray-200"
    >
      <template
        #default="{
          currentRefinement,
          nbPages,
          pages,
          isFirstPage,
          isLastPage,
          refine,
          createURL
        }"
      >
        <div class="p-2 border border-gray-200 lg:hidden">
          <a
            v-if="!isFirstPage"
            :href="createURL(currentRefinement - 1)"
            aria-label="Go to Previous Page"
          >
            <SvgIcon
              aria-hidden="true"
              icon-name="angle"
              class="w-4 h-4 rotate-90 text-gray-700"
            />
          </a>
          <span
            v-else
            class="text-gray-400"
          >
            <SvgIcon
              aria-hidden="true"
              icon-name="angle"
              class="w-4 h-4 rotate-90"
            />
          </span>
        </div>

        <template v-if="nbPages >= 2">
          <AisStats>
            <template #default="{ nbHits, page, hitsPerPage }">
              <div class="text-gray-800">
                <span class="hidden lg:block">
                  Showing {{ offset(page, hitsPerPage) + 1 }} - {{ offset(page, hitsPerPage) + hitsPerPage }} of {{ nbHits }}
                </span>
      
                <span class="lg:hidden">
                  Page <span class="font-medium">{{ page + 1 }}</span> of <span class="font-medium">{{ nbPages }}</span>
                </span>
              </div>
            </template>
          </AisStats>
          
          <ul class="items-center hidden gap-4 lg:flex">
            <li>
              <a
                v-if="!isFirstPage"
                :href="createURL(currentRefinement - 1)"
                aria-label="Go to Previous Page"
              >
                <SvgIcon
                  aria-hidden="true"
                  icon-name="angle"
                  class="w-4 h-4 rotate-90 text-gray-700"
                />
              </a>
              <span
                v-else
                class="text-gray-400"
              >
                <SvgIcon
                  aria-hidden="true"
                  icon-name="angle"
                  class="w-4 h-4 rotate-90"
                />
              </span>
            </li>

            <li>
              <a
                class="font-medium px-4 py-2.5"
                :class="{'bg-yellow-100': currentRefinement === 0 }"
                :href="createURL(0)"
                :aria-label="currentRefinement === 0 
                  ? `Current Page, Page 1` 
                  : `Go to page 1 of ${nbPages}`
                "
                :aria-current="currentRefinement === 0"
                @click.prevent="refine(0)"
              >
                1
              </a>
            </li>

            <li v-show="currentRefinement >= 3">
              <span class="font-medium px-4 py-2.5 cursor-default">
                ...
              </span>
            </li>
          
            <li
              v-for="page in refinePages(pages)"
              :key="page"
            >
              <a
                class="font-medium px-4 py-2.5 hover:bg-yellow-500 hover:text-white"
                :class="{'bg-yellow-100': page === currentRefinement }"
                :href="createURL(page)"
                :aria-label="page === currentRefinement 
                  ? `Current Page, Page ${page + 1}` 
                  : `Go to page ${page + 1} of ${nbPages}`
                "
                :aria-current="page === currentRefinement"
                @click.prevent="refine(page)"
              >
                {{ page + 1 }}
              </a>
            </li>

            <li v-show="currentRefinement < nbPages - 3">
              <span class="font-medium px-4 py-2.5 cursor-default">
                ...
              </span>
            </li>

            <li>
              <a
                :href="createURL(nbPages-1)"
                class="font-medium px-4 py-2.5 hover:bg-yellow-500 hover:text-white"
                :class="{'bg-yellow-100': nbPages - 1 === currentRefinement }"
                :aria-label="nbPages - 1 === currentRefinement 
                  ? `Current Page, Page ${nbPages}` 
                  : `Go to page ${nbPages} of ${nbPages}`
                "
                :aria-current="nbPages - 1 === currentRefinement"
                @click.prevent="refine(nbPages-1)"
              >
                {{ nbPages }}
              </a>
            </li>
          
            <li>
              <a
                v-if="!isLastPage"
                :href="createURL(currentRefinement + 1)"
                aria-label="Go to Next Page"
              >
                <SvgIcon
                  aria-hidden="true"
                  icon-name="angle"
                  class="w-4 h-4 -rotate-90 text-gray-700"
                />
              </a>
              <span
                v-else
                class="text-gray-400"
              >
                <SvgIcon
                  aria-hidden="true"
                  icon-name="angle"
                  class="w-4 h-4 -rotate-90"
                />
              </span>
            </li>
          </ul>
        </template>

        <div class="p-2 border border-gray-200 lg:hidden">
          <a
            v-if="!isLastPage"
            :href="createURL(currentRefinement + 1)"
            aria-label="Go to Next Page"
          >
            <SvgIcon
              aria-hidden="true"
              icon-name="angle"
              class="w-4 h-4 -rotate-90 text-gray-700"
            />
          </a>
          <span
            v-else
            class="text-gray-400"
          >
            <SvgIcon
              aria-hidden="true"
              icon-name="angle"
              class="w-4 h-4 -rotate-90"
            />
          </span>
        </div>
      </template>
    </AisPagination>
  </nav>
</template>

<script lang="ts">
export default { name: 'SearchPagination' }
</script>

<script setup lang="ts">
import { AisPagination, AisStats } from 'vue-instantsearch/vue3/es'
const offset = (currentPage: number, hitsPerPage: number) => (currentPage * hitsPerPage)

const refinePages = (pages) => {
  pages.shift()
  pages.pop()
  return pages
}
</script>
